import React, { FC, useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import Form from '../../components/form/form';
import StudentSelector from '../../components/student-selector/student-selector';
import SubmitButton from '../../components/submit-button/submit-button';
import Loading from '../../components/loading/loading';
import ErrorComponent from '../../components/error/error';
import PasswordRequirements from '../../components/password-requirements/password-requirements';

// Form Data
import { formDataEmployeeCreate } from '../../form-data/form-data-employee-create';

// Helpers
import {
    getEmployeeData,
    handleFormOnChange,
    checkIfAllFormHasFilled,
    handleStudentChange,
} from '../../helpers/employee-helper';
import { getErrorField, getErrorMessage } from '../../helpers/api-error-helper';

// Models
import ApiError from '../../models/api-error';
import {
    BaseContentContainerActionButtonPosition,
    BaseContentContainerBiggerSide,
    BaseContentContainerFormat,
} from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import ManagementService from '../../services/management-service';
import ClassroomService from "../../services/classroom-service";

const EmployeeCreate: FC = () => {
    const navigate = useNavigate();
    const { setToastContent } = useContext(GeneralContext);
    const managementService = new ManagementService();
    const classroomService = new ClassroomService();
    const [error, setError] = useState(false);
    const initialData: EmployeeFormData = getEmployeeData();
    const [data, setData] = useState<EmployeeFormData>(initialData);

    const [employee] = useState<Employee>(null);
    const [validations, setValidations] = useState({});
    const [allowSubmit, setAllowSubmit] = useState(true);
    useEffect(() => {
        checkIfAllFormHasFilled(data, validations, allowSubmit, setAllowSubmit);
    }, [validations, allowSubmit, data]); // eslint-disable-line react-hooks/exhaustive-deps

    const [classroomList, setClassroomList] = useState<
        ClassroomStudents[] | null
    >(null);
    useEffect(() => {
        classroomService
            .listClassroomStudents()
            .then((classroomsResponse) => {
                setClassroomList(classroomsResponse);
            })
            .catch((err) => {
                console.error(err);
                return setError(true);
            });
    }, [employee]); // eslint-disable-line react-hooks/exhaustive-deps

    const createEmployeePromise = (submitData: CreateEmployeeFormData) => {
        return managementService
            .createNewEmployee(submitData)
            .then(() => {
                setToastContent({
                    isOpen: true,
                    text: 'Funcionário salvo!',
                    type: 'success',
                });
                navigate('/gerenciar');
            })
            .catch((e: ApiError) => {
                const text = getErrorMessage(e, 'Houve um erro ao salvar o funcionário.');
                const field = getErrorField(e);

                if (field) {
                    setValidations((current) => ({ ...current, [field]: e.message }));
                }

                setToastContent({ isOpen: true, text, type: 'error' });
            });
    };

    const handleSubmit = () => {
        const submitData = Object.assign({}, data);

        const mapping: CreateEmployeeFormData = {
            name:  submitData.formData.name,
            email: submitData.formData.email,
            password: submitData.formData.password,
            role: submitData.formData.role,
            classroomIds: submitData.classData
        }

        createEmployeePromise(mapping);
    };

    if (error) return <ErrorComponent type='contentFull' />;

    if (!classroomList) return <Loading type='contentFull' />;

    return (
        <BaseContentContainer
            biggerSide={BaseContentContainerBiggerSide.right}
            format={BaseContentContainerFormat.doubleWithActionButtons}
            actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
        >
            <Form
                formData={formDataEmployeeCreate()}
                validations={validations}
                handleOnChange={(evt) =>
                    handleFormOnChange(
                        evt,
                        validations,
                        data,
                        setValidations,
                        setData,
                    )
                }
                fieldHints={{
                    password: (
                        <PasswordRequirements password={data.formData.password} />
                    ),
                }}
            />
            {classroomList && (
                <StudentSelector
                    classrooms={classroomList}
                    handleStudentChange={(
                        selectedClassrooms: number[],
                        selectedStudents: number[]
                    ) =>
                        handleStudentChange(
                            selectedClassrooms,
                            selectedStudents,
                            data,
                            setData
                        )
                    }
                    hideStudentSelection={true}
                />
            )}
            <ButtonsBar pattern='firstSeparate' verticalAlign='bottom'>
                <Link to='/gerenciar'>
                    <SubmitButton value='cancelar' variant='terciary' />
                </Link>
                <SubmitButton
                    value='salvar'
                    variant='primary'
                    onClick={handleSubmit}
                    disabled={!allowSubmit}
                />
            </ButtonsBar>
        </BaseContentContainer>
    );
};

export default EmployeeCreate;
